import axios, { AxiosError } from 'axios';

/**
 * Contrat de réponse d'erreur du serveur (ExceptionHandlingMiddleware).
 * Aligné sur `ApiErrorResponse` côté ASP.NET Core.
 */
interface ApiErrorResponse {
    /** Message lisible — provient de BusinessException.Message ou message générique */
    message: string;
    /** Code métier — présent uniquement sur les BusinessException */
    code?: string;
    /** Identifiant de trace distribuée (context.TraceIdentifier) */
    traceId?: string;
}

interface ValidationErrors {
    fields: Record<string, string[]>;
    truncated: boolean;
}

interface FormattedError {
    message: string;
    details?: {
        /** Code d'erreur : code métier serveur (BusinessException) ou code réseau Axios */
        code?: string;
        method?: string;
        url?: string;
        /** Données brutes de la réponse — à ne pas exposer côté client en prod */
        data?: unknown;
        timeout?: number;
        status?: number;
        validationErrors?: ValidationErrors;
    };
    /** Identifiant de trace distribué — utile pour le support et les logs serveur */
    traceId?: string;
    timestamp: string;
}

/** Limite d'affichage des erreurs de validation */
const MAX_VALIDATION_FIELDS = 5;
const MAX_VALIDATION_MESSAGES = 3;

/**
 * Tente de parser les données de réponse en `ApiErrorResponse` typé.
 * Retourne `null` si la structure ne correspond pas.
 */
const parseApiErrorResponse = (data: unknown): ApiErrorResponse | null => {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
    const d = data as Record<string, unknown>;
    if (typeof d.message !== 'string') return null;

    return {
        message: d.message,
        code: typeof d.code === 'string' ? d.code : undefined,
        traceId: typeof d.traceId === 'string' ? d.traceId : undefined,
    };
};

/**
 * Extrait un message d'erreur lisible à partir des données de réponse.
 * Priorité : ApiErrorResponse → structures alternatives connues.
 */
const extractErrorMessage = (data: unknown): string | undefined => {
    if (typeof data === 'string') return data;

    // Structure principale du serveur (ApiErrorResponse)
    const parsed = parseApiErrorResponse(data);
    if (parsed) return parsed.message;

    if (data && typeof data === 'object') {
        const d = data as Record<string, unknown>;

        if (typeof d.detail === 'string') return d.detail;

        // { error: "message" }
        if (typeof d.error === 'string') return d.error;

        // { error: { message: "message" } }
        if (d.error && typeof d.error === 'object') {
            const nested = d.error as Record<string, unknown>;
            if (typeof nested.message === 'string') return nested.message;
        }

        // { errors: ["msg1", "msg2"] | [{ message: "..." }] }
        if (Array.isArray(d.errors)) {
            return d.errors
                .map((e) => (typeof e === 'string' ? e : ((e as any)?.message ?? JSON.stringify(e))))
                .filter(Boolean)
                .join(', ');
        }
    }

    return undefined;
};

/**
 * Extrait et limite les erreurs de validation (HTTP 422).
 */
const extractValidationErrors = (data: unknown): ValidationErrors | undefined => {
    if (!data || typeof data !== 'object') return undefined;

    const d = data as Record<string, unknown>;
    if (!d.errors || typeof d.errors !== 'object' || Array.isArray(d.errors)) return undefined;

    const allEntries = Object.entries(d.errors as Record<string, unknown>);
    const truncated = allEntries.length > MAX_VALIDATION_FIELDS;

    const fields = allEntries.slice(0, MAX_VALIDATION_FIELDS).reduce(
        (acc, [key, val]) => {
            acc[key] = Array.isArray(val) ? (val as unknown[]).slice(0, MAX_VALIDATION_MESSAGES).map(String) : [String(val)];
            return acc;
        },
        {} as Record<string, string[]>,
    );

    return { fields, truncated };
};

/**
 * Formate une erreur Axios (ou générique) en objet structuré et lisible.
 *
 * ⚠️  `details.data` contient les données brutes de la réponse serveur.
 *     Ne pas les exposer directement à l'utilisateur final en production.
 */
export const formatAxiosError = (error: unknown): FormattedError => {
    const timestamp = new Date().toISOString();

    if (!(error instanceof Error)) {
        return { message: 'Erreur inconnue', timestamp };
    }

    // ─── Erreur Axios ────────────────────────────────────────────────────────
    if (axios.isAxiosError(error)) {
        const requestConfig = error.config;
        const details: FormattedError['details'] = {
            method: requestConfig?.method?.toUpperCase(),
            url: requestConfig?.url,
        };

        let message = 'Erreur de communication avec le serveur';
        let traceId: string | undefined;

        // Cas 1 : réponse reçue du serveur
        if (error.response) {
            const { status, statusText, data: errorData, headers } = error.response;

            // Tentative de parsing structuré (ApiErrorResponse)
            const apiError = parseApiErrorResponse(errorData);
            traceId = apiError?.traceId;

            // Le code métier (BusinessException) prend la priorité sur le code HTTP générique
            details.code = apiError?.code ?? `ERR_API_${status}`;

            const specificMessage = apiError?.message ?? extractErrorMessage(errorData);

            if (specificMessage) {
                message = specificMessage;
            } else {
                switch (status) {
                    case 400:
                        message = 'Requête incorrecte';
                        break;
                    case 401:
                        message = 'Authentification requise';
                        break;
                    case 403:
                        message = 'Accès refusé';
                        break;
                    case 404:
                        message = 'Ressource introuvable';
                        break;
                    case 409:
                        message = "Conflit avec l'état actuel de la ressource";
                        break;
                    case 410:
                        message = 'Ressource supprimée définitivement';
                        break;

                    case 422: {
                        message = 'Validation échouée';
                        details.validationErrors = extractValidationErrors(errorData);
                        break;
                    }

                    case 429: {
                        message = 'Trop de requêtes';
                        const retryAfter = headers?.['retry-after'];
                        if (retryAfter) message += ` — réessayez dans ${retryAfter}s`;
                        break;
                    }

                    case 500:
                        message = 'Erreur interne du serveur';
                        break;
                    case 502:
                        message = 'Passerelle incorrecte';
                        break;
                    case 503:
                        message = 'Service indisponible';
                        break;
                    case 504:
                        message = 'Délai de réponse dépassé';
                        break;

                    default:
                        message = `Erreur serveur [${status} ${statusText}]`;
                        break;
                }
            }

            details.status = status;
            details.data = errorData;

            // Cas 2 : requête envoyée mais aucune réponse reçue
        } else if (error.request) {
            const errorCode = (error as AxiosError).code;

            switch (errorCode) {
                case 'ECONNABORTED':
                    message = 'Délai de connexion dépassé';
                    break;
                case 'ETIMEDOUT':
                    message = 'Délai de réponse expiré';
                    break;
                case 'ENOTFOUND':
                    message = 'Impossible de résoudre le serveur';
                    break;
                case 'ECONNREFUSED':
                    message = 'Connexion refusée par le serveur';
                    break;
                case 'ERR_BAD_REQUEST':
                    message = 'Requête mal formée';
                    break;
                case 'ERR_BAD_RESPONSE':
                    message = 'Réponse mal formée';
                    break;
                case 'ERR_CANCELED':
                    message = 'Requête annulée';
                    break;
                case 'ERR_FR_TOO_MANY_REDIRECTS':
                    message = 'Trop de redirections';
                    break;
                case 'ERR_INVALID_URL':
                    message = 'URL invalide';
                    break;
                case 'ERR_NETWORK':
                    message = 'Problème réseau';
                    break;
                case 'EAI_AGAIN':
                    message = 'Problème DNS temporaire';
                    break;
                default:
                    message = 'Problème de communication avec le serveur';
                    break;
            }

            details.code = errorCode ?? 'ERR_NETWORK';
            details.timeout = requestConfig?.timeout;

            // Cas 3 : erreur de configuration (avant envoi)
        } else {
            message = 'Erreur de configuration de la requête';
            details.code = 'ERR_CONFIG';
        }

        return { message, details, traceId, timestamp };
    }

    // ─── AbortError (AbortController) ───────────────────────────────────────
    if (error.name === 'AbortError') {
        return {
            message: 'Requête annulée',
            details: { code: 'ERR_ABORTED' },
            timestamp,
        };
    }

    // ─── Erreur générique ────────────────────────────────────────────────────
    return {
        message: error.message || 'Erreur inconnue',
        details: { code: 'ERR_GENERIC' },
        timestamp,
    };
};
